import AppContext from 'App/AppContext';
import { getPendingApprovalCount, subscribePendingApprovals } from 'components/Approvals/pendingApprovalsStore';
import { useCallback, useContext, useLayoutEffect, useMemo, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ensureExpandStateLoaded,
  getCollapsedIds,
  navGroupStoreId,
  openGroupForVisit,
  subscribeExpandState,
  toggleExpanded as toggleExpandedInStore,
} from './navExpandStore';
import { useNavGroupCaptions, useNavItems } from './navItems';
import { navLayout } from './navLayout';
import {
  INavItemDefinition,
  INavModel,
  INavModelGroup,
  INavModelItem,
  NavBadgeId,
  NavGroupCaptionId,
  NavItemId,
} from './navModel';
import { getVisitedChild, setVisitedChild } from './navVisitStore';

//
// Resolves the catalogue (navItems.ts) and the order (navLayout.ts) into the menu one user sees now:
// the visible entries, the current one, and what is open. It also keeps, for the session, which
// view of an expandable layout entry the user opened last, so a click on the entry returns there.
// Independent of how the menu is drawn.
//

// The entries the layout nests other entries under. A click on one opens the child visited last in
// this session.
const layoutParentIds: ReadonlySet<string> = new Set(
  navLayout.body.reduce<string[]>((ids, group) => {
    group.items.forEach((entry) => {
      if (typeof entry !== 'string') {
        ids.push(entry.id);
      }
    });

    return ids;
  }, []),
);

type NavBadgeCounts = Record<NavBadgeId, number | undefined>;

// badgeCounts is left out for the selectable items: the selection and the navigation do not use the count
const toModelItem = (definition: INavItemDefinition, badgeCounts?: NavBadgeCounts): INavModelItem => ({
  id: definition.id,
  label: definition.label,
  iconName: definition.iconName,
  className: definition.className,
  route: definition.route,
  matchSegments: definition.matchSegments,
  badgeCount: definition.badge ? badgeCounts?.[definition.badge] : undefined,
});

/**
 * The item that owns the current location: the deepest match, so /admin/users picks Users rather
 * than a shorter /admin match. Inside an area the menu knows (the first segment belongs to some
 * entry of the catalogue, visible or not) a location without a match highlights nothing; anywhere
 * else falls back to Start.
 */
const findSelectedId = (
  items: INavModelItem[],
  knownAreas: ReadonlySet<string>,
  locationSegments: string[],
): string => {
  let matchedId: string | undefined = undefined;
  let matchedLength = 0;

  for (const item of items) {
    for (const segments of item.matchSegments ?? []) {
      const isMatch =
        segments.length > matchedLength && segments.every((segment, index) => locationSegments[index] === segment);

      if (isMatch) {
        matchedId = item.id;
        matchedLength = segments.length;
      }
    }
  }

  if (matchedId) {
    return matchedId;
  }

  const area = locationSegments[0];

  return area !== undefined && knownAreas.has(area) ? '' : 'start';
};

export const useNavModel = (): INavModel => {
  const appContext = useContext(AppContext);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const navItems = useNavItems();
  const captions = useNavGroupCaptions();

  ensureExpandStateLoaded(appContext);
  const collapsedIds = useSyncExternalStore(subscribeExpandState, getCollapsedIds);
  // Read here and not in useNavItems, so a count change rebuilds the groups but not the catalogue.
  const pendingApprovalCount = useSyncExternalStore(subscribePendingApprovals, getPendingApprovalCount);
  const badgeCounts = useMemo<NavBadgeCounts>(
    () => ({ pendingApprovals: pendingApprovalCount }),
    [pendingApprovalCount],
  );

  const locationSegments = useMemo(() => pathname.split('/').filter((segment) => segment.length > 0), [pathname]);

  // Everything the user may open, on screen or not: an entry that is scrolled away, collapsed into
  // an icon or inside a closed group still owns its route.
  const selectableItems = useMemo<INavModelItem[]>(() => {
    const all: INavModelItem[] = [];

    (Object.keys(navItems) as NavItemId[]).forEach((itemId) => {
      const definition = navItems[itemId];
      if (definition.isVisible(appContext)) {
        all.push(toModelItem(definition));
      }
    });

    return all;
  }, [appContext, navItems]);

  // The first URL segments the whole catalogue knows, so an entry hidden for this user still counts
  // as a known area rather than sending the selection back to Start.
  const knownAreas = useMemo(() => {
    const areas = new Set<string>();
    (Object.values(navItems) as INavItemDefinition[]).forEach((definition) => {
      definition.matchSegments.forEach((segments) => {
        if (segments.length > 0) {
          areas.add(segments[0]);
        }
      });
    });

    return areas;
  }, [navItems]);

  const matchedId = useMemo(
    () => findSelectedId(selectableItems, knownAreas, locationSegments),
    [knownAreas, locationSegments, selectableItems],
  );

  const bodyGroups = useMemo<INavModelGroup[]>(() => {
    const groups: INavModelGroup[] = [];

    navLayout.body.forEach((groupLayout) => {
      const groupItems: INavModelItem[] = [];

      groupLayout.items.forEach((entry) => {
        const itemId = typeof entry === 'string' ? entry : entry.id;
        const nestedIds = typeof entry === 'string' ? [] : entry.children;
        // Every nested entry keeps its own gate.
        const nestedChildren = nestedIds
          .map((childId) => navItems[childId])
          .filter((definition) => definition.isVisible(appContext))
          .map((child) => toModelItem(child, badgeCounts));

        const definition = navItems[itemId];
        if (!definition.isVisible(appContext)) {
          // The nested entries take the place of a parent the user may not see, so nesting never
          // changes who sees an entry.
          groupItems.push(...nestedChildren);

          return;
        }

        const item = toModelItem(definition, badgeCounts);
        if (nestedChildren.length > 0) {
          item.children = nestedChildren;
          item.isExpanded = !collapsedIds.has(itemId);
        }

        groupItems.push(item);
      });

      if (groupItems.length > 0) {
        const caption = groupLayout.caption;
        // A group without a caption has no header to click, so it can never be closed.
        const isCollapsible = caption !== undefined && groupLayout.isCollapsible !== false;

        groups.push({
          id: caption,
          caption: caption ? captions[caption] : undefined,
          isCollapsible,
          isExpanded: caption === undefined || !isCollapsible || !collapsedIds.has(navGroupStoreId(caption)),
          items: groupItems,
        });
      }
    });

    return groups;
  }, [appContext, badgeCounts, captions, collapsedIds, navItems]);

  // The group that holds the page the user is on, when that group can be closed.
  const matchedGroupId = useMemo<NavGroupCaptionId | undefined>(() => {
    const group = bodyGroups.find(
      (candidate) =>
        candidate.isCollapsible &&
        candidate.items.some(
          (item) => item.id === matchedId || (item.children ?? []).some((child) => child.id === matchedId),
        ),
    );

    return group?.id;
  }, [bodyGroups, matchedId]);

  // Arriving in a closed group opens it for this visit. The id alone is the dependency: while the
  // user stays in the same group the effect does not run again, so they can close it and keep
  // working on the page they are on.
  useLayoutEffect(() => {
    openGroupForVisit(matchedGroupId ? navGroupStoreId(matchedGroupId) : undefined);
  }, [matchedGroupId]);

  const footerItems = useMemo<INavModelItem[]>(() => {
    const footer: INavModelItem[] = [];

    navLayout.footer.forEach((groupLayout) => {
      const header = navItems[groupLayout.header];
      if (!header.isVisible(appContext)) {
        return;
      }

      const children = groupLayout.items
        .map((itemId) => navItems[itemId])
        .filter((definition) => definition.isVisible(appContext))
        .map((child) => toModelItem(child, badgeCounts));

      if (children.length === 0) {
        return;
      }

      // A pinned group never opens in place; the renderer shows its pages beside the header.
      footer.push({ ...toModelItem(header, badgeCounts), children });
    });

    return footer;
  }, [appContext, badgeCounts, navItems]);

  // The entry each nested entry is drawn under: a nested entry under its parent, a page of a pinned
  // group under its header.
  const parentById = useMemo(() => {
    const map = new Map<string, INavModelItem>();
    bodyGroups.forEach((group) => {
      group.items.forEach((item) => {
        item.children?.forEach((child) => map.set(child.id, item));
      });
    });
    footerItems.forEach((header) => {
      header.children?.forEach((child) => map.set(child.id, header));
    });

    return map;
  }, [bodyGroups, footerItems]);

  // The nearest entry the user can see: an entry under a closed parent highlights that parent, and a
  // page of a pinned group always does, because those pages are only ever in the callout.
  const selectedId = useMemo(() => {
    const parent = parentById.get(matchedId);
    if (parent && parent.isExpanded !== true) {
      return parent.id;
    }

    return matchedId;
  }, [matchedId, parentById]);

  // The layout entry whose view the user is on, if any. Its id alone goes into the effect below,
  // so a change of the open state, which rebuilds `parentById`, does not run the effect again.
  const visitedParentId = useMemo(() => {
    const parent = parentById.get(matchedId);

    return parent && layoutParentIds.has(parent.id) ? parent.id : undefined;
  }, [matchedId, parentById]);

  // Kept for the session, so a click on the entry returns to this view.
  useLayoutEffect(() => {
    if (visitedParentId) {
      setVisitedChild(visitedParentId, matchedId);
    }
  }, [matchedId, visitedParentId]);

  const selectItem = useCallback(
    (id: string) => {
      const item = selectableItems.find((candidate) => candidate.id === id);
      // A parent from the layout returns to the view visited last, if the user may still see it.
      const visitedChildId = layoutParentIds.has(id) ? getVisitedChild(id) : undefined;
      const visitedChild = visitedChildId
        ? selectableItems.find((candidate) => candidate.id === visitedChildId)
        : undefined;
      const target = visitedChild ?? item;
      if (!target?.route) {
        return;
      }

      navigate(target.route);
    },
    [navigate, selectableItems],
  );

  const toggleExpanded = useCallback(
    (id: string) => {
      toggleExpandedInStore(appContext, id);
    },
    [appContext],
  );

  const toggleGroup = useCallback(
    (id: NavGroupCaptionId) => {
      toggleExpandedInStore(appContext, navGroupStoreId(id));
    },
    [appContext],
  );

  return { bodyGroups, footerItems, matchedId, selectedId, selectItem, toggleExpanded, toggleGroup };
};
